import { useState } from 'react';

export const DEFAULT_LABEL_FIELD_ORDER = ['geotechnicalLayer', 'soilName', 'genesisAndStratigraphy', 'soilState', 'thickness'];

// Domyślna typografia per element (size px, fontFamily, fontWeight).
// Wartości odpowiadają hardcoded sprzed wprowadzenia menadżera typografii.
export const DEFAULT_TYPOGRAPHY = {
  boreholeName:        { size: 21, family: 'sans-serif', weight: 'bold' },
  boreholeElevation:   { size: 16, family: 'sans-serif', weight: 'normal' },
  elevationScale:      { size: 16, family: 'sans-serif', weight: 'bold' },
  crossSectionTitle:   { size: 20, family: 'sans-serif', weight: 'bold' },
  crossSectionScale:   { size: 13, family: 'sans-serif', weight: 'normal' },
  boreholeMeta:        { size: 11, family: 'sans-serif', weight: 'normal' },
};

export const FONT_FAMILY_OPTIONS = [
  { value: 'sans-serif', label: 'Sans-serif (default)' },
  { value: 'serif', label: 'Serif' },
  { value: 'monospace', label: 'Monospace' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, sans-serif', label: 'Helvetica' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, sans-serif', label: 'Tahoma' },
  { value: '"Times New Roman", serif', label: 'Times New Roman' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: '"Courier New", monospace', label: 'Courier New' },
];

export const TYPOGRAPHY_LABELS = {
  boreholeName: 'Nazwa otworu (O-1w)',
  boreholeElevation: 'Rzędna otworu (344.31)',
  elevationScale: 'Skala wysokościowa (m n.p.m.)',
  crossSectionTitle: 'Tytuł przekroju',
  crossSectionScale: 'Skala przekroju (1:N)',
  boreholeMeta: 'Metadane otworu (👤📅)',
};

const normalizeLabelFieldOrder = (arr) => {
  if (!Array.isArray(arr)) return [...DEFAULT_LABEL_FIELD_ORDER];
  const known = arr.filter(k => DEFAULT_LABEL_FIELD_ORDER.includes(k));
  const missing = DEFAULT_LABEL_FIELD_ORDER.filter(k => !known.includes(k));
  return [...known, ...missing];
};

/**
 * Hook zarządzający stanami wyświetlania (toggle'e UI).
 * Zero zależności od innych hooków.
 */
export function useDisplaySettings(initialData) {
  const s = initialData?.settings || {};
  const [showGrid, setShowGrid] = useState(s.showGrid ?? false);
  const [showBoreholeRectangles, setShowBoreholeRectangles] = useState(s.showBoreholeRectangles ?? true);
  const [showSoilName, setShowSoilName] = useState(s.showSoilName ?? true);
  const [showSoilState, setShowSoilState] = useState(s.showSoilState ?? false);
  const [showGeotechnicalLayer, setShowGeotechnicalLayer] = useState(s.showGeotechnicalLayer ?? false);
  const [showGenesisAndStratigraphy, setShowGenesisAndStratigraphy] = useState(s.showGenesisAndStratigraphy ?? false);
  const [showThickness, setShowThickness] = useState(s.showThickness ?? true);
  const [showDepths, setShowDepths] = useState(true);
  const [showElevationScale, setShowElevationScale] = useState(true);
  const [showElevationScaleLeft, setShowElevationScaleLeft] = useState(s.showElevationScaleLeft ?? false);
  const [showCrossSectionTitle, setShowCrossSectionTitle] = useState(s.showCrossSectionTitle ?? false);
  const [crossSectionTitle, setCrossSectionTitle] = useState(s.crossSectionTitle ?? 'PRZEKRÓJ GEOTECHNICZNY');
  const [crossSectionTitlePos, setCrossSectionTitlePos] = useState(s.crossSectionTitlePos ?? null);
  const [typography, setTypography] = useState(() => {
    const seed = s.typography || {};
    const merged = {};
    for (const k of Object.keys(DEFAULT_TYPOGRAPHY)) {
      merged[k] = { ...DEFAULT_TYPOGRAPHY[k], ...(seed[k] || {}) };
    }
    return merged;
  });
  const [showDrawingTools, setShowDrawingTools] = useState(false);
  const [usePastelColors, setUsePastelColors] = useState(s.usePastelColors ?? false);
  const [outlineOnly, setOutlineOnly] = useState(false);
  const [sharpenContours, setSharpenContours] = useState(s.sharpenContours ?? false);
  const [movePolygonMode, setMovePolygonMode] = useState(false);
  const [snapToOrtho, setSnapToOrtho] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [gridSize, setGridSize] = useState(1.0); // metry
  const [snapToEdge, setSnapToEdge] = useState(false);
  const [snapToBorehole, setSnapToBorehole] = useState(true); // domyślnie ON — historyczne zachowanie
  const [showLiveMeasure, setShowLiveMeasure] = useState(false);
  const [showLenses, setShowLenses] = useState(true);
  const [fullscreenMode, setFullscreenMode] = useState(false);

  // Stany paneli accordion
  const [showBoreholeEditPanel, setShowBoreholeEditPanel] = useState(false);
  const [showDrawingToolsPanel, setShowDrawingToolsPanel] = useState(false);
  const [showGeologicalObjectsPanel, setShowGeologicalObjectsPanel] = useState(false);
  const [showBoreholeMapPanel, setShowBoreholeMapPanel] = useState(false);
  const [showTerrain, setShowTerrain] = useState(true);
  const [showWater, setShowWater] = useState(s.showWater ?? false);
  const [showBoreholeMeta, setShowBoreholeMeta] = useState(s.showBoreholeMeta ?? false);
  const [showPolygonLabels, setShowPolygonLabels] = useState(s.showPolygonLabels ?? false);
  const [labelFieldOrder, setLabelFieldOrder] = useState(normalizeLabelFieldOrder(s.labelFieldOrder));
  const [smoothWaterLine, setSmoothWaterLine] = useState(false);
  const [showFreeWaterLine, setShowFreeWaterLine] = useState(false);
  const [showConfinedWaterLine, setShowConfinedWaterLine] = useState(false);

  return {
    showGrid, setShowGrid,
    showBoreholeRectangles, setShowBoreholeRectangles,
    showSoilName, setShowSoilName,
    showSoilState, setShowSoilState,
    showGeotechnicalLayer, setShowGeotechnicalLayer,
    showGenesisAndStratigraphy, setShowGenesisAndStratigraphy,
    showThickness, setShowThickness,
    showDepths, setShowDepths,
    showElevationScale, setShowElevationScale,
    showElevationScaleLeft, setShowElevationScaleLeft,
    showCrossSectionTitle, setShowCrossSectionTitle,
    crossSectionTitle, setCrossSectionTitle,
    crossSectionTitlePos, setCrossSectionTitlePos,
    typography, setTypography,
    showDrawingTools, setShowDrawingTools,
    usePastelColors, setUsePastelColors,
    outlineOnly, setOutlineOnly,
    sharpenContours, setSharpenContours,
    movePolygonMode, setMovePolygonMode,
    snapToOrtho, setSnapToOrtho,
    snapToGrid, setSnapToGrid,
    gridSize, setGridSize,
    snapToEdge, setSnapToEdge,
    snapToBorehole, setSnapToBorehole,
    showLiveMeasure, setShowLiveMeasure,
    showLenses, setShowLenses,
    fullscreenMode, setFullscreenMode,
    showBoreholeEditPanel, setShowBoreholeEditPanel,
    showDrawingToolsPanel, setShowDrawingToolsPanel,
    showGeologicalObjectsPanel, setShowGeologicalObjectsPanel,
    showBoreholeMapPanel, setShowBoreholeMapPanel,
    showTerrain, setShowTerrain,
    showWater, setShowWater,
    showBoreholeMeta, setShowBoreholeMeta,
    showPolygonLabels, setShowPolygonLabels,
    labelFieldOrder, setLabelFieldOrder,
    smoothWaterLine, setSmoothWaterLine,
    showFreeWaterLine, setShowFreeWaterLine,
    showConfinedWaterLine, setShowConfinedWaterLine,
  };
}
